type Cleanup = () => void;

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

function isIOS(): boolean {
  // iPadOS can report as Mac; maxTouchPoints helps differentiate.
  const ua = navigator.userAgent || "";
  const isIPhoneIPadIPod = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isIPhoneIPadIPod || isIPadOS;
}

function isStandaloneMode(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    (navigator as NavigatorWithStandalone).standalone === true
  );
}

function getScrollableAncestor(start: Element | null): HTMLElement | null {
  let el: Element | null = start;

  while (el && el !== document.body && el !== document.documentElement) {
    if (!(el instanceof HTMLElement)) {
      el = el.parentElement;
      continue;
    }

    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const canScrollY = (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight;

    if (canScrollY) {
      return el;
    }

    if (el instanceof HTMLElement && el.hasAttribute("data-overlay-root")) {
      return null;
    }

    el = el.parentElement;
  }

  // Prefer the app-level scroll container if it exists.
  const appScroll = document.querySelector<HTMLElement>(".pwa-scroll-container");
  if (appScroll) {
    return appScroll;
  }

  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
}

/**
 * Prevent iOS "rubber band" overscroll in home-screen installed (standalone) mode.
 * This keeps normal scrolling inside scrollable containers, but blocks pull-to-bounce at edges.
 */
export function enableIosStandaloneRubberBandPrevention(): Cleanup {
  if (!isIOS() || !isStandaloneMode()) {
    return () => {};
  }

  let startY = 0;
  let activeScroller: HTMLElement | null = null;

  const isRootScroller = (el: HTMLElement) =>
    el === document.body ||
    el === document.documentElement ||
    el === document.scrollingElement ||
    el.tagName === "BODY" ||
    el.tagName === "HTML";

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) {
      activeScroller = null;
      return;
    }

    startY = e.touches[0].clientY;
    activeScroller = getScrollableAncestor(e.target as Element | null);
  };

  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 1) {
      return;
    }

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;

    // If we can't find a scrollable element, block to avoid viewport bounce.
    if (!activeScroller) {
      e.preventDefault();
      return;
    }

    // Allow the "rubber band" bounce for in-app content scrolling.
    // We only want to prevent the *viewport/page* rubber band in standalone mode.
    if (!isRootScroller(activeScroller)) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = activeScroller;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    // Pulling down at top, or pushing up at bottom -> prevent rubberband.
    if ((deltaY > 0 && atTop) || (deltaY < 0 && atBottom)) {
      e.preventDefault();
    }
  };

  document.addEventListener("touchstart", onTouchStart, { passive: true });
  document.addEventListener("touchmove", onTouchMove, { passive: false });

  return () => {
    document.removeEventListener("touchstart", onTouchStart);
    document.removeEventListener("touchmove", onTouchMove);
  };
}


